import { useState, useEffect } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  CalendarClock,
  MapPin,
  CheckCircle2,
  Clock,
  Eye,
  Package,
  Phone,
  Navigation,
  Calendar as CalendarIcon,
  Info,
} from "lucide-react";

const ADDRESS_DEFAULT = "Rue Henri Joseph Genesse 34, 1070 Anderlecht";
const PHONE_DEFAULT = "0470 37 57 35";

// Format une date ISO YYYY-MM-DD en français : "lundi 27 juillet"
const formatDateFr = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString("fr-FR", {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
  } catch {
    return iso;
  }
};

const PriseRdv = () => {
  const [searchParams] = useSearchParams();
  const initialType = searchParams.get("type") === "retrait" ? "retrait" : searchParams.get("type") === "visite" ? "visite" : "";

  const [cfg, setCfg] = useState(null);
  const [typeRdv, setTypeRdv] = useState(initialType); // "" | "visite" | "retrait"
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [heure, setHeure] = useState("");
  const [form, setForm] = useState({
    nom: "",
    telephone: "",
    email: "",
    commentaire: "",
    modele_recherche: "",
    numero_commande: "",
  });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);
  const [chairs, setChairs] = useState([]);       // fallback catalogue public
  const [prod1, setProd1] = useState(null);        // Produit affiché 1
  const [prod2, setProd2] = useState(null);        // Produit affiché 2

  useEffect(() => {
    axios.get(`${API}/public/rdv/config`).then(async (r) => {
      setCfg(r.data);
      // Charge les 2 produits configurés (nom + image)
      const fetchProd = (id) => axios.get(`${API}/public/product-brief/${id}`).then((x) => x.data).catch(() => null);
      const [p1, p2] = await Promise.all([
        r.data.produit_affiche_1_id ? fetchProd(r.data.produit_affiche_1_id) : Promise.resolve(null),
        r.data.produit_affiche_2_id ? fetchProd(r.data.produit_affiche_2_id) : Promise.resolve(null),
      ]);
      setProd1(p1);
      setProd2(p2);
    }).catch(() => setCfg({ actif: false }));
    // Fallback catalogue public (best-effort)
    axios.get(`${API}/public/products`).then((r) => setChairs(r.data || [])).catch(() => setChairs([]));
  }, []);

  const today = new Date().toISOString().slice(0, 10);

  const onPickDate = async (d) => {
    setDate(d);
    setHeure("");
    setSlots([]);
    if (!d) return;
    setLoadingSlots(true);
    try {
      const r = await axios.get(`${API}/public/rdv/slots`, { params: { date: d } });
      setSlots(r.data.slots || []);
    } catch {
      toast.error("Erreur de chargement des créneaux");
    } finally {
      setLoadingSlots(false);
    }
  };

  const book = async (e) => {
    e.preventDefault();
    if (!typeRdv) {
      toast.error("Choisissez d'abord un type de rendez-vous");
      return;
    }
    if (!form.nom || !form.telephone) {
      toast.error("Nom et téléphone obligatoires");
      return;
    }
    if (!date || !heure) {
      toast.error("Choisissez une date et un créneau");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/public/rdv/book`, {
        nom: form.nom,
        telephone: form.telephone,
        email: form.email,
        commentaire: form.commentaire,
        date,
        heure,
        type_rdv: typeRdv,
        modele_recherche: typeRdv === "visite" ? form.modele_recherche : "",
        numero_commande: typeRdv === "retrait" ? form.numero_commande : "",
      });
      setDone({ date, heure, type_rdv: typeRdv });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Ce créneau n'est plus disponible");
      onPickDate(date);
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
      </div>
    );
  }

  const entreprise = cfg.entreprise || "TopCocoon";
  const adresseComplete = cfg.adresse || ADDRESS_DEFAULT;
  const telephone = cfg.telephone || PHONE_DEFAULT;
  const itineraireUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(adresseComplete)}`;

  // Produits affichés : préférence à la config admin ; fallback catalogue public par nom
  const displayChair1 = prod1 || chairs.find((c) => (c.modele || "").toLowerCase().includes("milan")) || null;
  const displayChair2 = prod2 || chairs.find((c) => (c.modele || "").toLowerCase().includes("king") || (c.modele || "").toLowerCase().includes("louis") || (c.modele || "").toLowerCase().includes("ghost")) || null;

  const nameOf = (c, fallback) => {
    if (!c) return fallback;
    const m = (c.modele || "").trim();
    const col = (c.couleur || "").trim();
    return `Chaise ${m}${col ? ` — ${col}` : ""}`;
  };

  // ---- Écran de confirmation ----
  if (done) {
    const dateFr = formatDateFr(done.date);
    const heureFr = done.heure;
    const typeLabel = done.type_rdv === "retrait" ? "Retrait de commande" : "Visite du showroom";

    const icsContent = () => {
      const start = `${done.date.replace(/-/g, "")}T${heureFr.replace(":", "")}00`;
      const [h, m] = heureFr.split(":").map(Number);
      const endHM = new Date(2000, 0, 1, h, m + 30);
      const endH = String(endHM.getHours()).padStart(2, "0");
      const endM = String(endHM.getMinutes()).padStart(2, "0");
      const end = `${done.date.replace(/-/g, "")}T${endH}${endM}00`;
      return [
        "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//TopCocoon//RDV//FR",
        "BEGIN:VEVENT",
        `UID:rdv-${done.date}-${done.heure}-${Date.now()}@topcocoon`,
        `DTSTART:${start}`,
        `DTEND:${end}`,
        `SUMMARY:${typeLabel} — TopCocoon`,
        `LOCATION:${adresseComplete}`,
        `DESCRIPTION:${typeLabel} chez TopCocoon`,
        "END:VEVENT", "END:VCALENDAR",
      ].join("\r\n");
    };

    const addToCalendar = () => {
      const blob = new Blob([icsContent()], { type: "text/calendar" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "topcocoon-rdv.ics";
      a.click();
      URL.revokeObjectURL(url);
    };

    return (
      <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-slate-50 py-6 px-4" data-testid="prise-rdv-page">
        <div className="max-w-lg mx-auto">
          <Card data-testid="rdv-confirmation" className="border-emerald-200">
            <CardContent className="py-8 text-center space-y-4 px-5">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 mx-auto">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800">Votre rendez-vous est confirmé !</h2>

              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 text-left space-y-2">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500">Type de rendez-vous</p>
                    <p className="font-semibold text-slate-800" data-testid="confirmation-type">{typeLabel}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <CalendarIcon className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500">Date et heure</p>
                    <p className="font-semibold text-slate-800" data-testid="confirmation-datetime">{dateFr} à {heureFr}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500">Adresse</p>
                    <p className="font-semibold text-slate-800">{adresseComplete}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Phone className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500">Téléphone</p>
                    <p className="font-semibold text-slate-800">{telephone}</p>
                  </div>
                </div>
              </div>

              <p className="text-sm text-slate-500">
                Une confirmation vous sera envoyée. À très bientôt&nbsp;!
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-2">
                <Button variant="outline" onClick={() => window.open(itineraireUrl, "_blank")} data-testid="confirmation-itineraire-btn">
                  <Navigation className="w-4 h-4 mr-1" />Itinéraire
                </Button>
                <Button variant="outline" onClick={addToCalendar} data-testid="confirmation-calendar-btn">
                  <CalendarIcon className="w-4 h-4 mr-1" />Ajouter au calendrier
                </Button>
                <Button variant="outline" onClick={() => window.location.href = `tel:${telephone.replace(/\s+/g, "")}`} data-testid="confirmation-modifier-btn">
                  <Phone className="w-4 h-4 mr-1" />Modifier / annuler
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (cfg.actif === false) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-slate-50 py-10 px-4" data-testid="prise-rdv-page">
        <div className="max-w-md mx-auto">
          <Card>
            <CardContent className="py-12 text-center text-slate-500">Les réservations en ligne sont momentanément fermées.</CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ---- Récap avant confirmation ----
  const recapVisible = typeRdv && date && heure;

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-slate-50 py-6 px-4" data-testid="prise-rdv-page">
      <div className="max-w-lg mx-auto space-y-5">
        {/* Header */}
        <div className="text-center pt-2">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-indigo-600 text-white mb-3">
            <CalendarClock className="w-7 h-7" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 leading-tight">
            Réservez votre passage chez {entreprise}
          </h1>
          <p className="text-sm text-slate-500 mt-2 leading-relaxed max-w-md mx-auto">
            Venez découvrir et essayer nos chaises ou choisissez un créneau pour retirer votre commande à notre dépôt-showroom d&apos;Anderlecht.
          </p>
        </div>

        {/* Choix du type de rendez-vous */}
        <div className="grid grid-cols-1 gap-3" data-testid="type-choices">
          <button
            type="button"
            onClick={() => setTypeRdv("visite")}
            className={`text-left p-4 rounded-2xl border-2 transition-all active:scale-[0.99] ${
              typeRdv === "visite"
                ? "border-indigo-600 bg-indigo-50 shadow-md"
                : "border-slate-200 bg-white hover:border-indigo-300"
            }`}
            data-testid="choice-visite"
          >
            <div className="flex items-start gap-3">
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${typeRdv === "visite" ? "bg-indigo-600 text-white" : "bg-indigo-100 text-indigo-600"}`}>
                <Eye className="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-slate-800">Voir et essayer les chaises</p>
                <p className="text-sm text-slate-500 leading-snug mt-0.5">
                  Découvrez nos modèles, testez leur confort et comparez les couleurs, sans obligation d&apos;achat.
                </p>
              </div>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setTypeRdv("retrait")}
            className={`text-left p-4 rounded-2xl border-2 transition-all active:scale-[0.99] ${
              typeRdv === "retrait"
                ? "border-emerald-600 bg-emerald-50 shadow-md"
                : "border-slate-200 bg-white hover:border-emerald-300"
            }`}
            data-testid="choice-retrait"
          >
            <div className="flex items-start gap-3">
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${typeRdv === "retrait" ? "bg-emerald-600 text-white" : "bg-emerald-100 text-emerald-600"}`}>
                <Package className="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-slate-800">Retirer ma commande</p>
                <p className="text-sm text-slate-500 leading-snug mt-0.5">
                  Choisissez une heure de retrait afin que votre commande soit préparée avant votre arrivée.
                </p>
              </div>
            </div>
          </button>
        </div>

        {/* Section chaises — compacte, produits configurés depuis Rendez-vous (dépôt) */}
        <Card data-testid="chairs-section">
          <CardContent className="py-4 px-4">
            <p className="text-sm font-bold text-slate-800 mb-3">Découvrez nos modèles au showroom</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="text-center">
                <div className="aspect-square w-full rounded-xl overflow-hidden bg-slate-100 flex items-center justify-center border border-slate-200">
                  {displayChair1?.image_url ? (
                    <img src={displayChair1.image_url} alt={nameOf(displayChair1, "Chaise")} className="w-full h-full object-cover" data-testid="chair-img-1" />
                  ) : (
                    <Package className="w-10 h-10 text-slate-300" />
                  )}
                </div>
                <p className="text-sm font-semibold text-slate-800 mt-2" data-testid="chair-name-1">{nameOf(displayChair1, "Chaise")}</p>
              </div>
              <div className="text-center">
                <div className="aspect-square w-full rounded-xl overflow-hidden bg-slate-100 flex items-center justify-center border border-slate-200">
                  {displayChair2?.image_url ? (
                    <img src={displayChair2.image_url} alt={nameOf(displayChair2, "Chaise")} className="w-full h-full object-cover" data-testid="chair-img-2" />
                  ) : (
                    <Package className="w-10 h-10 text-slate-300" />
                  )}
                </div>
                <p className="text-sm font-semibold text-slate-800 mt-2" data-testid="chair-name-2">{nameOf(displayChair2, "Chaise")}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Calendrier + Formulaire — visible seulement si un type est choisi */}
        {typeRdv && (
          <Card>
            <CardContent className="py-5 px-4 space-y-5">
              <p className="text-sm font-bold text-slate-800 flex items-center gap-2">
                <Clock className="w-4 h-4 text-indigo-600" />
                Choisissez votre créneau
              </p>

              <div className="space-y-2">
                <Label className="text-sm">Date</Label>
                <Input
                  type="date"
                  min={today}
                  value={date}
                  onChange={(e) => onPickDate(e.target.value)}
                  className="h-12 text-base"
                  data-testid="rdv-date-picker"
                />
                {date && (
                  <p className="text-xs text-slate-500" data-testid="rdv-date-fr">
                    📅 {formatDateFr(date)}
                  </p>
                )}
              </div>

              {date && (
                <div className="space-y-2">
                  <Label className="text-sm flex items-center gap-1">Créneaux disponibles</Label>
                  {loadingSlots ? (
                    <p className="text-sm text-slate-400">Chargement…</p>
                  ) : slots.length === 0 ? (
                    <p className="text-sm text-amber-600">Aucun créneau disponible ce jour-là. Choisissez une autre date.</p>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {slots.map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => setHeure(s)}
                          className={`h-12 rounded-lg text-base font-medium border-2 transition-colors ${
                            heure === s
                              ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
                              : "bg-white text-slate-700 border-slate-200 hover:border-indigo-300"
                          }`}
                          data-testid={`rdv-slot-${s}`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Formulaire selon type */}
              {heure && (
                <form onSubmit={book} className="space-y-4 border-t pt-4">
                  {typeRdv === "visite" ? (
                    <>
                      <div className="space-y-1.5">
                        <Label className="text-sm">Nom complet *</Label>
                        <Input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} className="h-11" required data-testid="rdv-nom-input" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-sm">Téléphone *</Label>
                        <Input value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} className="h-11" required data-testid="rdv-tel-input" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-sm">Email (facultatif)</Label>
                        <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11" data-testid="rdv-email-input" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-sm">Modèle recherché (facultatif)</Label>
                        <Select value={form.modele_recherche} onValueChange={(v) => setForm({ ...form, modele_recherche: v })}>
                          <SelectTrigger className="h-11" data-testid="rdv-modele-select">
                            <SelectValue placeholder="Choisir un modèle…" />
                          </SelectTrigger>
                          <SelectContent>
                            {displayChair1 && <SelectItem value={displayChair1.modele || "Chaise 1"}>{displayChair1.modele || "Chaise 1"}</SelectItem>}
                            {displayChair2 && <SelectItem value={displayChair2.modele || "Chaise 2"}>{displayChair2.modele || "Chaise 2"}</SelectItem>}
                            <SelectItem value="Je ne sais pas encore">Je ne sais pas encore</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-sm">Commentaire (facultatif)</Label>
                        <Textarea value={form.commentaire} onChange={(e) => setForm({ ...form, commentaire: e.target.value })} rows={2} data-testid="rdv-commentaire-input" />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="space-y-1.5">
                        <Label className="text-sm">Numéro de commande (facultatif)</Label>
                        <Input value={form.numero_commande} onChange={(e) => setForm({ ...form, numero_commande: e.target.value })} className="h-11" placeholder="Ex: #1234" data-testid="rdv-numero-commande-input" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-sm">Nom utilisé lors de la commande *</Label>
                        <Input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} className="h-11" required data-testid="rdv-nom-input" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-sm">Téléphone *</Label>
                        <Input value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} className="h-11" required data-testid="rdv-tel-input" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-sm">Email (facultatif)</Label>
                        <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11" data-testid="rdv-email-input" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-sm">Commentaire (facultatif)</Label>
                        <Textarea value={form.commentaire} onChange={(e) => setForm({ ...form, commentaire: e.target.value })} rows={2} data-testid="rdv-commentaire-input" />
                      </div>
                    </>
                  )}

                  {/* Récapitulatif */}
                  {recapVisible && (
                    <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-200 text-sm" data-testid="rdv-recap">
                      <p className="font-bold text-indigo-800 mb-1">
                        {typeRdv === "retrait" ? "Retrait de commande" : "Visite du showroom"}
                      </p>
                      <p className="text-slate-700">{formatDateFr(date)} à {heure}</p>
                      <p className="text-slate-500 text-xs mt-0.5">Dépôt-showroom TopCocoon, Anderlecht</p>
                    </div>
                  )}

                  {typeRdv === "retrait" && (
                    <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900 leading-relaxed" data-testid="rdv-retrait-notice">
                      Votre commande sera préparée avant votre arrivée. Pensez à prévoir un véhicule suffisamment grand pour transporter vos chaises.
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={saving}
                    className={`w-full h-12 text-base font-semibold ${typeRdv === "retrait" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-indigo-600 hover:bg-indigo-700"}`}
                    data-testid="rdv-book-btn"
                  >
                    {saving
                      ? "Réservation…"
                      : typeRdv === "retrait"
                        ? "Confirmer le retrait de ma commande"
                        : "Réserver ma visite"}
                  </Button>

                  {/* Rassurance */}
                  <ul className="text-[11px] text-slate-500 space-y-0.5 pt-1">
                    <li>✓ Réservation gratuite</li>
                    <li>✓ Aucun paiement demandé en ligne</li>
                    <li>✓ Confirmation immédiate</li>
                    <li>✓ Modification ou annulation possible</li>
                    <li>✓ Dépôt-showroom accessible uniquement sur rendez-vous</li>
                  </ul>
                </form>
              )}
            </CardContent>
          </Card>
        )}

        {/* Coordonnées + itinéraire */}
        <Card data-testid="rdv-coordonnees">
          <CardContent className="py-4 px-4 space-y-3">
            <div className="text-sm">
              <p className="font-bold text-slate-800">TopCocoon</p>
              <p className="text-slate-600">Rue Henri Joseph Genesse 34</p>
              <p className="text-slate-600">1070 Anderlecht</p>
              <p className="text-slate-600 mt-1">{telephone}</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => window.open(itineraireUrl, "_blank")} data-testid="rdv-itineraire-btn">
                <Navigation className="w-4 h-4 mr-1" />Voir l&apos;itinéraire
              </Button>
              <Button variant="outline" onClick={() => window.location.href = `tel:${telephone.replace(/\s+/g, "")}`} data-testid="rdv-appeler-btn">
                <Phone className="w-4 h-4 mr-1" />Nous appeler
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PriseRdv;
